import {useGetInfiniteDrugsCategories} from '@/modules/basic-data/categories/api'
import {doseCalculationMethodOptions} from '@/modules/drug-catalog/substances/constants'
import {useGetUnits} from '@/modules/products/units/api'
import {mapUnitsToSelectItems} from '@/modules/products/units/functions'
import {Route} from '@/routes/configuration/drug-catalog/active-substances'
import {FilterButton} from '@cypro/ui/components/ui/filter-button'
import {ResourcesFiltersContainer} from '@/shared/components/ui/resources-filters-container'
import {useAppForm} from '@cypro/ui/integrations/form'
import {NO_CATEGORIES_FILTER} from '@cypro/engine/queries'
import {Box, HStack, IconButton} from '@chakra-ui/react'
import {useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {LuCircleX} from 'react-icons/lu'

export function ActiveSubstancesFilters() {
  const {t} = useTranslate('common')

  const search = Route.useSearch()
  const navigate = useNavigate()

  const form = useAppForm({
    defaultValues: {
      search: search.filters?.search || '',
      categories: search.filters?.categories || [],
      calculationMethod: search.filters?.calculationMethod || [],
      unit: search.filters?.unit || [],
    },
    listeners: {
      onChange({formApi, fieldApi}) {
        // if triggered by the searchbox, prevent the navigation because we want to debouce the "name" filter
        // and we do it directly on the <form.AppField />
        if (fieldApi.name === 'search') return

        const {values} = formApi.state

        navigate({
          to: '/configuration/drug-catalog/active-substances',
          search: prev => ({
            ...prev,
            filters: {
              calculationMethod: values.calculationMethod,
              categories: values.categories,
              search: values.search,
              unit: values.unit,
            },
          }),
        })
      },
    },
  })

  const values = useStore(form.store, state => state.values)
  const hasValues = Boolean(
    values?.search || values?.calculationMethod?.length || values?.categories?.length || values?.unit?.length
  )

  function onClearFilters() {
    form.setFieldValue('search', '')
    form.setFieldValue('calculationMethod', [])
    form.setFieldValue('categories', [])
    form.setFieldValue('unit', [])
  }

  const infiniteCategories = useGetInfiniteDrugsCategories()
  const {units} = useGetUnits()

  return (
    <ResourcesFiltersContainer>
      <form.AppForm>
        <HStack>
          <form.AppField name="categories">
            {field => (
              <field.InfiniteCombobox
                label={t('categories')}
                hideLabel
                multiple
                {...infiniteCategories}
                staticItems={[{value: NO_CATEGORIES_FILTER, label: t('no_categories')}]}
              >
                <FilterButton isApplied={Boolean(form.state.values.categories?.length)}>{t('categories')}</FilterButton>
              </field.InfiniteCombobox>
            )}
          </form.AppField>

          <form.AppField name="calculationMethod">
            {field => (
              <field.Select
                items={doseCalculationMethodOptions.map(item => ({value: item, label: t(item)}))}
                label={t('calculation')}
                hideLabel
                multiple
              >
                <FilterButton isApplied={Boolean(form.state.values.calculationMethod?.length)}>
                  {t('calculation')}
                </FilterButton>
              </field.Select>
            )}
          </form.AppField>

          <form.AppField name="unit">
            {field => (
              <field.Select items={mapUnitsToSelectItems(units)} label={t('unit')} hideLabel multiple>
                <FilterButton isApplied={Boolean(form.state.values.unit?.length)}>{t('unit')}</FilterButton>
              </field.Select>
            )}
          </form.AppField>

          {hasValues && (
            <IconButton variant="ghost" title={t('clear_filters')} onClick={onClearFilters}>
              <LuCircleX />
            </IconButton>
          )}
        </HStack>

        <Box>
          <form.AppField
            name="search"
            listeners={{
              onChange: ({fieldApi}) =>
                navigate({
                  to: '/configuration/drug-catalog/active-substances',
                  search: prev => ({...prev, filters: {...prev.filters, search: fieldApi.state.value}}),
                }),
              onChangeDebounceMs: 300,
            }}
          >
            {field => <field.Searchbox />}
          </form.AppField>
        </Box>
      </form.AppForm>
    </ResourcesFiltersContainer>
  )
}
